<template>
  <div
    class="sidebar-settings"
    data-test="graphics-sheetsSettings"
  >
    <div class="sidebar-settings-title">
      Data Source
    </div>

    <!-- Show button to connect sheet -->
    <template v-if="!isConnected">
      <div class="flex">
        <button
          class="btn btn-style-a btn-sm flex-grow"
          data-test="graphics-sheetsSettings-openConnectModal"
          data-entity="graphics-sheetsSettings-openConnectModal"
          @click="$emit('open-google-sheets-modal')"
        >
          Connect Sheet
        </button>
      </div>
    </template>

    <!-- Sync button, disconnect button, etc. -->
    <template v-else>
      <div
        v-if="workbookName"
        class="margin-bottom-md"
        data-test="graphics-sheetsSettings-workbookName"
      >
        <div class="sidebar-form-label margin-bottom-md">
          Spreadsheet

          <div
            class="flex flex-align-center google-sheets__workbook-name margin-left-sm"
          >
            <span>{{ workbookName }}</span>
            <a
              v-if="element.data.url"
              :href="element.data.url"
              class="margin-left-sm"
              target="_blank"
              data-test="graphics-sheetsSettings-workbookLink"
              data-entity="graphics-sheetsSettings-workbookLink"
            >
              <span class="fas fa-external-link" />
            </a>
          </div>
        </div>
      </div>

      <template v-if="isPrivateSheet">
        <div
          v-if="workbookData?.workbook?.sheets?.length"
          class="flex margin-bottom-md"
          data-test="graphics-sheetsSettings-sheetsSelection"
        >
          <label class="sidebar-form-label">
            Sheet

            <select v-model="sheetIndex">
              <option
                v-for="(sheet, index) in workbookData.workbook.sheets"
                :key="sheet.id"
                :value="index"
                data-test="graphics-sheetsSettings-selectSheet"
              >
                {{ sheet.title }}
              </option>
            </select>
          </label>
        </div>
      </template>
      <template v-else>
        <div
          v-if="sheets && sheets.length"
          class="flex margin-bottom-md"
          data-test="graphics-sheetsSettings-sheetsSelection"
        >
          <label class="sidebar-form-label">
            Sheet

            <select v-model="sheetIndex">
              <option
                v-for="(sheet, index) in sheets"
                :key="index + sheet"
                :value="index"
                data-test="graphics-sheetsSettings-selectSheet"
              >
                {{ sheet }}
              </option>
            </select>
          </label>
        </div>
      </template>

      <div
        v-if="element.data && element.data.owner"
        class="flex margin-bottom-md"
        data-test="graphics-sheetsSettings-owner"
      >
        <label class="sidebar-form-label">
          Linked By

          <div
            class="flex flex-align-center google-sheets__workbook-name margin-left-sm"
          >
            <span>{{ element.data.owner.displayName }}</span>
          </div>
        </label>
      </div>

      <div class="flex">
        <button
          class="btn btn-style-a btn-sm flex-grow"
          :class="error ? 'btn-danger' : ''"
          title="Sync Data with Sheets"
          :disabled="syncing"
          data-test="graphics-sheetsSettings-syncData"
          data-entity="graphics-sheetsSettings-syncData"
          @click="syncData"
        >
          <span v-if="error">Error</span>
          <span v-else-if="!syncing">Sync Data</span>
          <span v-else>Syncing...</span>
        </button>

        <button
          class="btn btn-style-b btn-sm btn-icon btn-danger"
          title="Disconnect"
          data-test="graphics-sheetsSettings-disconnect"
          data-entity="graphics-sheetsSettings-disconnect"
          @click="disconnect"
        >
          <span class="far fa-trash-alt" />
        </button>
      </div>

      <div class="flex flex-center-all">
        <div class="sidebar-input-sub-label">
          Last sync {{ lastSyncLabel }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import settingsMixins from '../../../../mixins/settings-mixins';

import catchify from 'catchify';

export default {
  emits: ['open-google-sheets-modal'],
  mixins: [settingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },

    template: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      error: null,
      syncing: false,

      // Make the timer reactive
      now: Date.now(),
    };
  },

  computed: {
    ...mapState({
      sheetsData(state) {
        return state[this.element.id]?.sheetsData;
      },
      sheets(state) {
        return state[this.element.id]?.sheets;
      },
      sheetsDetails(state) {
        return state[this.element.id]?.sheetsDetails;
      },
      workbookData(state) {
        return state[this.element.id]?.workbook;
      },
      forceConnect(state) {
        return state[this.element.id]?.forceConnect;
      },
    }),

    workbookName() {
      return this.element.data.workbookName;
    },

    isPrivateSheet() {
      return this.element.data.type === 'private-google-sheet';
    },

    /**
     * Get the amount of time since last sync and pretty it up.
     * Can probably be broken out to a shared util if needed elsewhere.
     */
    lastSyncLabel() {
      const { lastSync } = this.element.data;
      if (!lastSync) {
        return '';
      }

      const delta = (this.now - lastSync) / 1e3;
      let seconds = Math.floor(delta);

      if (seconds <= 0) {
        return 'less than a minute ago';
      }

      let minutes = Math.floor(seconds / 60);
      seconds -= minutes * 60;

      let hours = Math.floor(minutes / 60);
      minutes -= hours * 60;

      const days = Math.floor(hours / 24);
      hours -= days * 24;

      if (days > 7) {
        // Just show the date
        // TODO: Format prettier
        return new Date(lastSync).toISOString();
      }

      if (days >= 1) {
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }

      if (hours >= 1) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      }

      if (minutes >= 1) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      }

      return 'less than a minute ago';
    },

    isConnected() {
      return this.element.data?.isConnected;
    },
    sheetIndex: {
      get() {
        return this.element.data.sheetIndex || 0;
      },
      set(value) {
        this.updateOneElementValue('data.sheetIndex', value);
      },
    },
  },

  watch: {
    sheetIndex(newIdx) {
      const sheet = this.isPrivateSheet
        ? this.workbookData?.workbook?.sheets?.[newIdx]?.title
        : this.sheets[newIdx];

      const sheetId = this.isPrivateSheet
        ? this.workbookData?.workbook?.sheets?.[newIdx]?.sheetId
        : this.sheetsDetails[newIdx]?.properties?.sheetId;

      // `sheetId` can legitimately be `0`, so only bail when it is missing.
      if (sheetId === undefined || sheetId === null) { return; }

      this.updateManyElementValues({
        'data.sheet': sheet,
        'data.sheetId': sheetId,
      });

      this.syncData();
    },
  },

  created() {
    if (this.isPrivateSheet) {
      this.getPrivateWorkbookSheets({
        workbookId: this.element.data.workbookId,
        accountId: this.element.data.accountId,
      });
    }
  },

  mounted() {
    // We don't need a fancy timer so we don't need a 1s ticker
    this.interval = setInterval(() => {
      this.now = Date.now();
    }, 5e3);

    if (this.forceConnect) {
      this.$emit('open-google-sheets-modal');
    }
  },

  beforeUnmount() {
    clearInterval(this.interval);
  },

  methods: {
    ...mapActions({
      getPrivateSheetData(dispatch, payload) {
        return dispatch(`${this.element.id}/getPrivateSheetData`, payload);
      },

      getPrivateWorkbookSheets(dispatch, payload) {
        return dispatch(`${this.element.id}/getPrivateWorkbookSheets`, payload);
      },

      getPublicSheetData(dispatch, payload) {
        return dispatch(`${this.element.id}/getPublicSheetData`, payload);
      },

      clearSheetData(dispatch) {
        return dispatch(`${this.element.id}/clearSheetData`);
      },
    }),

    setError(error) {
      this.error = error;
      clearTimeout(this.errorTimeout);

      this.errorTimeout = setTimeout(() => {
        this.error = null;
      }, 3e3);
    },

    /**
     * disconnect removes metadata from element and sets isConnected back to false
     */
    disconnect() {
      this.updateOneElementValue('data.isConnected', false);

      this.removeManyElementProps([
        'data.url',
        'data.type',
        'data.sheet',
        'data.sheetId',
        'data.workbookId',
        'data.accountId',
        'data.lastSync',
      ]);

      this.clearSheetData();
    },

    /**
     * Force sync of data.
     * This will bust any cached values in the backend and refresh the data.
     */
    async syncData() {
      this.syncing = true;

      const {
        url,
        sheet,
        workbookId,
        accountId,
        sheetId,
      } = this.element.data;

      let err = null;

      if (accountId) {
        // Private sheet data
        [err] = await catchify(
          this.getPrivateSheetData({
            workbookId,
            accountId,
            sheetId,
            cache: false,
          }),
        );
      } else if (url) {
        [err] = await catchify(
          this.getPublicSheetData({
            url,
            sheet,
            force: true,
            key: this.element.id,
          }),
        );
      } else {
        err = new Error('Invalid meta data');
      }

      if (err) {
        this.setError(err);
        this.syncing = false;
        return;
      }

      this.now = Date.now();
      this.updateOneElementValue('data.lastSync', this.now);
      this.syncing = false;
    },
  },
};
</script>
